import { CurrencyTitle, formatAddress } from '@servicetitan/cp-ui';
import { DateTimeUtils } from '@servicetitan/datetime-utils';
import { BodyText, Button, Card, Eyebrow, Stack, Tag } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, SyntheticEvent } from 'react';
import { InvoiceV2 } from '../../../api/portal-invoices.api';
import { DashboardCustomerAddressesStore } from '../../../stores/dashboard-customer-addresses.store';
import { getLocationServiceAddress } from '../../../utils/dashboard-customer-addresses';
import { getDaysUntilDue, showOverdueTag } from '../../../utils/invoices';
import * as Styles from './invoices-list-item.module.less';

interface InvoicesListItemProps {
    className?: string;
    invoice: InvoiceV2;
    onViewInvoiceClick: (invoiceId: string) => void;
}

export const InvoicesListItem: FC<InvoicesListItemProps> = observer(props => {
    const { className, invoice, onViewInvoiceClick } = props;

    const [dateTimeUtils, dashboardCustomerAddressesStore] = useDependencies(
        DateTimeUtils,
        DashboardCustomerAddressesStore
    );

    const handleViewClick = (event: SyntheticEvent) => {
        event.stopPropagation();
        onViewInvoiceClick(invoice.id!);
    };

    const daysDue = getDaysUntilDue(invoice);

    const customerAddress = dashboardCustomerAddressesStore.getCustomerAddress(invoice.customerId);
    const customerName = customerAddress?.name ?? '';
    const locationAddress =
        getLocationServiceAddress(customerAddress?.addresses, invoice.serviceLocationId) ??
        invoice.address;

    const allowPayment = !invoice.achDisabled || !invoice.creditCardDisabled;

    return (
        <Card
            padding="thin"
            className={className}
            onClick={handleViewClick}
            data-cy="unpaid-invoices-list-item"
        >
            <Card.Section>
                <Stack spacing={1} justifyContent="space-between" alignItems="center">
                    <CurrencyTitle value={invoice.balance} size="medium" />
                    {showOverdueTag(invoice) ? (
                        <Tag color="critical" subtle>
                            Past Due
                        </Tag>
                    ) : (
                        <Tag subtle>
                            {daysDue === 0 && 'Due Today'}
                            {daysDue >= 1 && `Due in ${daysDue} day${daysDue > 1 ? 's' : ''}`}
                        </Tag>
                    )}
                </Stack>
            </Card.Section>
            <Card.Section>
                {!!invoice.dueDate && (
                    <div>
                        <Eyebrow size="small" className="m-b-half">
                            Due Date
                        </Eyebrow>
                        <BodyText
                            size="small"
                            className={classNames('m-b-half', {
                                'c-red-500': showOverdueTag(invoice),
                            })}
                        >
                            {`${dateTimeUtils.formatShortMonth(invoice.dueDate)} ${invoice.dueDate.getDate()}, ${invoice.dueDate.getFullYear()}`}
                        </BodyText>
                    </div>
                )}
                {!!customerName && (
                    <div className="p-t-1">
                        <Eyebrow size="small" className="m-b-half">
                            Customer
                        </Eyebrow>
                        <BodyText
                            size="small"
                            className={classNames('m-b-half', Styles.overflowText)}
                        >
                            {customerName}
                        </BodyText>
                    </div>
                )}
                {!!locationAddress && (
                    <div className="p-t-1">
                        <Eyebrow size="small" className="m-b-half">
                            Service Address
                        </Eyebrow>
                        <BodyText size="small" className={classNames(Styles.overflowText)}>
                            {formatAddress(locationAddress)}
                        </BodyText>
                    </div>
                )}
            </Card.Section>
            {allowPayment && (
                <Card.Section light>
                    <Stack justifyContent="flex-end">
                        <Button
                            primary
                            onClick={handleViewClick}
                            data-cy="invoices-list-item-pay-invoice"
                        >
                            Pay Invoice
                        </Button>
                    </Stack>
                </Card.Section>
            )}
        </Card>
    );
});
